import { MouseEventHandler, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Button, DatePicker, Form, Input, Select, Switch, Table, Tooltip, notification } from 'antd'
import styled from 'styled-components'
import moment from 'moment'

import { faCopy, faMagnifyingGlass, faPlus } from '@fortawesome/pro-duotone-svg-icons'
import { CheckOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { useFormQuery } from '../../../hooks/form-filer'
import { SharedTable } from '../../shared/table'
import { txt } from '../../../locales/i18n'

import * as NotificationService from '../../../services/bb-api/notification'

type Notification = { id: number; active: boolean; content: any }

export const NotificationsPage = () => {
	const history = useHistory()
	const [loading, setLoading] = useState(true)
	const [notifications, setNotifications] = useState<Notification[]>([])

	const [form] = Form.useForm<Notification>()
	const setQueryParams = useFormQuery(form)

	useEffect(() => {
		form.submit()
	}, [form])

	const activeToggle = async (notificationId: number, active: boolean) => {
		try {
			await NotificationService.update(notificationId, { active })
			form.submit()

			const message = txt('notification.update successful')
			notification.success({ message })
		} catch (error) {
			const message = txt('notification.update failed')
			notification.error({ message })
		}
	}

	const search = async (formValues: any) => {
		setQueryParams(formValues)
		setLoading(true)

		try {
			const notifications = await NotificationService.query(formValues)
			setNotifications(notifications)
		} catch (error) {
			const message = txt('notification.unknown error')
			notification.error({ message })
		}

		setLoading(false)
	}

	const renderActive = (notification: Notification) => (
		<span onClick={(event) => event.stopPropagation()}>
			<Switch
				loading={loading}
				checked={notification.active}
				onChange={(active) => activeToggle(notification.id, active)}
			/>
		</span>
	)

	const renderDate = (value: string | Date): string => {
		const format = 'dddd DD/MM-YY HH:mm '
		return moment(value).format(format)
	}

	const renderCopyAction = (notificationId: number) => {
		const copyHandler: MouseEventHandler = async (event) => {
			event.stopPropagation()

			try {
				await NotificationService.copy(notificationId)
				form?.submit()

				const message = txt('notification.create successful')
				notification.success({ message })
			} catch (error) {
				const message = txt('notification.create failed')
				notification.error({ message })
			}
		}

		return (
			<Tooltip title={txt('global.copy')}>
				<Button type="link" onClick={copyHandler}>
					<FontAwesomeIcon icon={faCopy} />
				</Button>
			</Tooltip>
		)
	}

	function renderCheckmark(value: number): JSX.Element | null {
		if (Boolean(value) === true) {
			return <CheckOutlined />;
		}

		return null;
	}

	return (
		<MainStyled>
			<section className="card search-container">
				<Tooltip title={txt('global.create')}>
					<Button type="link" href="notifications/create">
						<FontAwesomeIcon icon={faPlus} />
					</Button>
				</Tooltip>

				<Form form={form} onFinish={search} initialValues={{ status: 'active' }}>
					<Tooltip title={txt('global.status')}>
						<span>
							<Form.Item noStyle name="status">
								<Select size="small" dropdownMatchSelectWidth={false}>
									<Select.Option value="all">{txt('global.all')}</Select.Option>
									<Select.Option value="active">{txt('global.active')}</Select.Option>
									<Select.Option value="inactive">{txt('global.inactive')}</Select.Option>
								</Select>
							</Form.Item>
						</span>
					</Tooltip>

					<Tooltip title={txt('global.name')}>
						<span>
							<Form.Item noStyle name="name">
								<Input size="small" placeholder={txt('global.name')} />
							</Form.Item>
						</span>
					</Tooltip>

					<Tooltip title={txt('global.schedule')}>
						<span>
							<Form.Item noStyle name="date">
								<DatePicker.RangePicker size="small" allowEmpty={[true, true]} />
							</Form.Item>
						</span>
					</Tooltip>

					<Tooltip title={txt('global.search')}>
						<Button htmlType="submit" type="link">
							<FontAwesomeIcon icon={faMagnifyingGlass} />
						</Button>
					</Tooltip>
				</Form>
			</section>

			<div className="table-container">
				<SharedTable
					rowKey="id"
					loading={loading}
					dataSource={notifications}
					onRow={({ id }) => ({ onClick: () => history.push('notifications/' + id) })}
				>
					<Table.Column width={80} title={txt('global.active')} render={renderActive} align="center" />
					<Table.Column width={210} dataIndex="scheduled" title={txt('global.schedule')} render={renderDate} />
					<Table.Column width={180} title={txt('global.name')} dataIndex="name" ellipsis />
					<Table.Column width={180} title={txt('global.exposure')} dataIndex="exposure" />

					<Table.Column
						width={180}
						title={txt('global.title')}
						dataIndex={['content', 'lang', 'da', 'title']}
						ellipsis
					/>

					<Table.Column
						dataIndex="sent"
						title={'Sendt'}
						width="8ch"
						render={renderCheckmark}
						align="center"
					/>

					<Table.Column width={64} title={txt('global.copy')} dataIndex="id" render={renderCopyAction} align="center" />
				</SharedTable>
			</div>
		</MainStyled>
	)
}

const MainStyled = styled.main`
	background-color: ghostwhite;
	padding: 1rem;

	display: flex;
	flex-direction: column;
	gap: 1rem;

	.card {
		border-radius: 1rem;
		box-shadow: 0 0 1rem #89a3;

		background-color: white;
		padding: 0.6rem;

		display: flex;
		flex-direction: column;
	}

	.search-container {
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: center;
		gap: 1rem;

		form {
			display: contents;
		}
	}

	.table-container {
		border-radius: 1rem;
		box-shadow: 0 0 1rem #89a3;
		overflow: hidden;

		> * {
			overflow: auto;
		}
	}
`
